import { useState, useEffect, useRef, useCallback, ReactNode, type KeyboardEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'
import useAuthStore from '../../store/authStore.js'
import { useSettingsStore } from '../../store/settingsStore.js'
import { usePolicyStore } from '../../store/usePolicyStore.js'
import type { PolicySnapshot } from '../../store/usePolicyStore.js'
import { useSiteFilterStore } from '../../store/siteFilterStore.js'
import { useLanguage } from '../../i18n/index.jsx'
import { useToast } from '../ui/index.jsx'
import { cls } from '../../utils/helpers.js'
import { useLoans } from '../../hooks/useData.js'
// Brighella v2 (ADR-013 cross-cutting #4 + #6 + ADR-014 D12) — universal
// drawer + density binding + undo toast. All read cohesion + undo stores.
import EntityDrawer from '../cohesion/EntityDrawer'
import UndoToast from '../cohesion/UndoToast'
import KeyboardHelp from '../cohesion/KeyboardHelp'
import HelpModeKeyListener from '../help/HelpModeKeyListener'
import OnboardingTour from '../cohesion/OnboardingTour'
import OfflineBanner from '../cohesion/OfflineBanner'
import { useCohesionStore } from '../../store/cohesionStore'
import { useSiteFilterUrl } from '../../hooks/useSiteFilterUrl.js'
import UserProfileModal from './UserProfileModal.jsx'
import ProfileCompletionModal from './ProfileCompletionModal.jsx'
import OnboardingWizard from '../onboarding/OnboardingWizard.jsx'
import ImpersonationBanner from './ImpersonationBanner.jsx'
import SiteFilterBanner from '../ui/SiteFilterBanner.jsx'
import MustReadBanner from '../feed/MustReadBanner.js'
import api from '../../utils/api.js'
import { logError } from '../../utils/logError.js'
import { TYPE_ICONS } from './navConstants.jsx'
import AppSidebar from './AppSidebar.jsx'
import MobileTopbar from './MobileTopbar.jsx'
import MobileMenu from './MobileMenu.jsx'
import MobileBottomNav from './MobileBottomNav.jsx'
import CmdKSearch from './CmdKSearch.jsx'
import FloatingActions from './FloatingActions.jsx'
import { useInbox } from '../../hooks/useInbox.js'
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts.js'
import { useUsageNudge } from '../../hooks/useUsageNudge.js'
import QAOverlay from '../qa/QAOverlay'
import DemoSidebar from '../demo/DemoSidebar.jsx'
import StepUpProvider from '../auth/StepUpProvider.jsx'
import type { Notification } from '../../types/domain'
import './layout.css'

interface AppLayoutProps {
  children: ReactNode
}

interface AppLayoutSearchResult {
  type: string
  id?: number | string
  name: string
  icon?: ReactNode
  _entityType?: string
  _entityId?: number | string
  _path?: string
  [key: string]: unknown
}

// BILL-5: global past-due banner. The subscription demotes feature gating the
// moment Stripe reports past_due, but the only signal used to live on the
// admin-only billing page — staff just saw features vanish. This banner is
// visible to EVERY user; admins additionally get an "update payment" CTA.
function PastDueBanner() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const subStatus = useSettingsStore(s => s.subStatus)
  const isAdmin = useAuthStore(s => s.isAdmin?.() ?? false)
  if (subStatus !== 'past_due') return null
  return (
    <div
      role="alert"
      data-testid="past-due-banner"
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        padding: '0.55rem 1rem', background: 'var(--warning-dim)',
        color: 'var(--warning)', borderBottom: '1px solid var(--border-default)',
        fontSize: 'var(--text-sm)', fontWeight: 500,
      }}
    >
      <span style={{ flex: 1, minWidth: 200 }}>
        {isAdmin ? t('pastDueBannerAdmin') : t('pastDueBannerMember')}
      </span>
      {isAdmin && (
        <button
          type="button"
          onClick={() => navigate('/settings/billing')}
          style={{
            padding: '4px 12px', borderRadius: 8, border: 'none',
            background: 'var(--warning)', color: 'var(--text-inverse)',
            fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('pastDueUpdatePayment')}
        </button>
      )}
    </div>
  )
}

// Validates logo URL before use as <img src> to prevent injection via localStorage
function isSafeLogoUrl(url: unknown): url is string {
  if (!url || typeof url !== 'string') return false
  if (url.startsWith('/')) return true
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const platformMode = location.pathname.startsWith('/super-admin')
  const { user, updateUser } = useAuthStore()
  const { lang, setLang, t, languageOptions } = useLanguage()
  const { show: showToast } = useToast()
  const { appName } = useSettingsStore()
  const { siteIds, toggleSite, trackRecent } = useSiteFilterStore()
  useSiteFilterUrl()
  const siteIdParam = siteIds.length ? siteIds.join(',') : null
  const [menuOpen,          setMenuOpen]          = useState<boolean>(false)
  const [mobileSearchOpen,  setMobileSearchOpen]  = useState<boolean>(false)
  const [logoUrl,           setLogoUrl]           = useState<string | null>(() => { const stored = useSettingsStore.getState().logoUrl || localStorage.getItem('sw-logo-url'); return isSafeLogoUrl(stored) ? stored : null })
  const storeLogoUrl = useSettingsStore(s => s.logoUrl)
  useEffect(() => { setLogoUrl(storeLogoUrl && isSafeLogoUrl(storeLogoUrl) ? storeLogoUrl : null) }, [storeLogoUrl])
  const [theme,             setTheme]             = useState<string>(() => localStorage.getItem('stagewise-theme') || 'dark')
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState<boolean>(() => localStorage.getItem('sw-sidebar-collapsed') === 'true')
  const [showOnboarding,    setShowOnboarding]    = useState<boolean>(false)
  const [profileComplete,   setProfileComplete]   = useState<boolean>(true)
  const [, setNotifications] = useState<Notification[]>([])
  const [showProfile, setShowProfile] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchResults, setAppLayoutSearchResults] = useState<AppLayoutSearchResult[]>([])
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false)
  const [searchHighlight, setSearchHighlight] = useState<number>(-1)
  const [cmdKOpen, setCmdKOpen] = useState<boolean>(false)
  const cmdKRef = useRef<HTMLInputElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wsMessageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null)
  const { data: loanData } = useLoans(siteIdParam ? { site_id: siteIdParam } : {})
  const overdueCount = loanData?.overdue_count || 0

  // App-wide inbox subscription — keeps messengerStore.totalUnread (sidebar/mobile
  // unread badge) live even when no comms surface is mounted (replaces the dock's
  // DockInboxList subscription after D10 removal).
  useInbox({ archived: false })

  // ── Search logic ──
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    if (debounceRef.current !== null) clearTimeout(debounceRef.current)
    if (!query || query.trim().length < 2) {
      setAppLayoutSearchResults([])
      setShowSearchDropdown(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ results?: Array<{ type: string; id: number | string; name: string }> }>('/search', { params: { q: query.trim() } })
        setAppLayoutSearchResults((data.results || []).map(r => ({
          type: r.type, id: r.id, name: r.name, icon: TYPE_ICONS[r.type] || <Search size={16} />,
        })))
        setShowSearchDropdown(true)
      } catch {
        // silently handled
      }
    }, 300)
  }, [])

  const handleSearchSelect = (result: AppLayoutSearchResult) => {
    setSearchQuery('')
    setShowSearchDropdown(false)
    setAppLayoutSearchResults([])
    const routes: Record<string, string> = {
      item:       `/items/${result.id}`,
      production: `/productions/${result.id}`,
      bundle:     `/bundles`,
      location:   `/locations`,
      user:       `/users/${result.id}/activity`,
      document:   `/documents`,
    }
    navigate(routes[result.type] || '/')
  }

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSearchDropdown || !searchResults.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSearchHighlight(h => (h + 1) % searchResults.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSearchHighlight(h => (h <= 0 ? searchResults.length - 1 : h - 1))
    } else if (e.key === 'Enter' && searchHighlight >= 0) {
      e.preventDefault()
      handleSearchSelect(searchResults[searchHighlight])
    } else if (e.key === 'Escape') {
      setShowSearchDropdown(false)
      setSearchHighlight(-1)
    }
  }

  const handleCmdKSelect = (result: AppLayoutSearchResult) => {
    setCmdKOpen(false)
    if (result._path) { navigate(result._path); return }
    handleSearchSelect(result)
  }

  // ── Theme ──
  const toggleSidebar = () => {
    const next = !sidebarCollapsed
    setSidebarCollapsed(next)
    localStorage.setItem('sw-sidebar-collapsed', String(next))
  }

  const toggleTheme = () => {
    let newTheme
    if (theme === 'dark') newTheme = 'medium'
    else if (theme === 'medium') newTheme = 'light'
    else newTheme = 'dark'
    setTheme(newTheme)
    localStorage.setItem('stagewise-theme', newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
  }

  const getThemeEmoji = () => {
    if (theme === 'dark') return '🌑'
    if (theme === 'medium') return '🌓'
    return '☀️'
  }
  const getThemeTitle = () => {
    if (theme === 'dark') return t('themeDark')
    if (theme === 'medium') return t('themeMedium')
    return t('themeLight')
  }

  // ── Cleanup debounce timer on unmount ──
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  // ── Load theme on mount (no auth needed) ──
  useEffect(() => {
    const savedTheme = localStorage.getItem('stagewise-theme') || 'dark'
    setTheme(savedTheme)
    document.documentElement.setAttribute('data-theme', savedTheme)
  }, [])

  // ── Load settings & notifications AFTER auth completes ──
  // This MUST depend on `user` to avoid a race condition where these API calls
  // fire before fetchMe() completes its cookie refresh. Without a Bearer token,
  // they'd get 401 → the interceptor would try to refresh with the already-revoked
  // cookie → fail → hard-redirect to /login, killing the auth flow.
  useEffect(() => {
    if (!user) return

    // Only fetch settings+billing once per session to prevent sidebar flicker.
    // AppLayout remounts on every route change, so without this guard the
    // sequential API calls would briefly reset enabledModules to null.
    if (!useSettingsStore.getState().settingsLoaded) {
      Promise.all([
        api.get('/settings'),
        // BILL-4: member-readable plan snapshot. /billing/status is admin-only,
        // so fetching it here 403'd for every non-admin → planName fell back to
        // 'free' and staff on PAID plans saw upgrade banners. /billing/plan
        // returns { plan, status, features } to all authenticated users.
        api.get('/billing/plan').catch((err) => {
          console.warn('[AppLayout] billing plan fetch failed, falling back to free plan:', err?.response?.status, err?.message)
          return { data: {} }
        }),
      ]).then(([settingsRes, billingRes]) => {
        const d = (settingsRes.data || {}) as Record<string, unknown>
        const store = useSettingsStore.getState()

        // Apply non-module settings
        const logoUrl_ = d.logo_url as string | undefined
        const currency_ = d.currency as string | undefined
        const appName_ = d.app_name as string | undefined
        const companyName_ = d.company_name as string | undefined
        const timezone_ = d.timezone as string | undefined
        if (isSafeLogoUrl(logoUrl_)) { setLogoUrl(logoUrl_); localStorage.setItem('sw-logo-url', logoUrl_) }
        store.setCurrency(currency_ || 'DKK')
        store.setAppName(appName_ || 'Brighella')
        store.setCompanyName(companyName_ || '')
        store.setLogoUrl(logoUrl_ || '')
        store.setTimezone(timezone_ || Intl.DateTimeFormat().resolvedOptions().timeZone)
        store.setQaMode(d.qa_mode === true)
        store.setPlatformDisabledModules((d.platform_disabled_modules as Record<string, unknown>) || {})
        if (d.gb_enabled !== undefined) store.setGbEnabled(d.gb_enabled as boolean)
        if (d.gb_default_standard) store.setGbDefaultStandard(d.gb_default_standard as string)
        if (d.gb_country_code !== undefined) store.setGbCountryCode(d.gb_country_code as string)
        if (!d.onboarding_complete && useAuthStore.getState().isAdmin()) {
          setShowOnboarding(true)
        }

        // Billing plan info
        const billingData = (billingRes.data || {}) as Record<string, unknown>
        const plan = (billingData.plan as string) || 'free'
        store.setPlanName(plan)
        store.setPlanFeatures(billingData.features as Record<string, unknown> | undefined)
        // BILL-5: subscription status drives the global past_due banner.
        store.setSubStatus((billingData.status as string) || null)

        // Gating migration Phase 6 — module availability is owned by the
        // canonical policy snapshot (GET /api/policy/modules, usePolicyStore),
        // not reconstructed here from a client-side plan map. `enabled_modules`
        // is stored only as the container for `disabled_items` (item-level nav
        // hiding, surfaced via settingsStore.disabledItems).
        store.setEnabledModules((d.enabled_modules as Record<string, unknown> | null) || null)

        store.setSettingsLoaded(true)
      }).catch(err => logError('AppLayout.loadSettings', err))
    }

    // Platform modules — runs independently, non-blocking, fail-silent
    api.get('/platform/modules').then(res => {
      const pm = (res.data as Record<string, unknown> | undefined)
      useSettingsStore.getState().setPlatformModules((pm?.platform_modules as Record<string, unknown>) || {})
    }).catch(() => {
      // Non-critical — default is all modules enabled
    })

    // Canonical module-policy snapshot (gating migration Phase 3) — runs
    // independently, non-blocking, fail-silent. When it loads, the route
    // guards and nav gating read it instead of reconstructing access from
    // scattered store fields; when absent they fall back to the legacy path.
    api.get('/policy/modules').then(res => {
      usePolicyStore.getState().setPolicy(res.data as PolicySnapshot | null | undefined)
    }).catch(() => {
      // Non-critical — gating falls back to the settings-derived path.
    })

    const fetchNotifications = () => {
      api.get('/notifications').then(res => {
        const nd = res.data as Record<string, unknown> | undefined
        setNotifications((nd?.notifications as Notification[]) || [])
      }).catch(err => logError('AppLayout.loadNotifications', err))
    }
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [user])

  // ── Profile completion modal ──
  // Only show after onboarding is done, never for guests, and respect skip
  const settingsLoaded = useSettingsStore(s => s.settingsLoaded)
  useEffect(() => {
    if (!user || user.role === 'guest') return
    // Phase 28 adjacent fix: global admins manage tenants from /super-admin/*;
    // work_title and department are tenant-staff fields that don't apply to them.
    if (user.is_global_admin === true || user.role === 'global_admin') return
    if (!settingsLoaded) return          // wait until we know onboarding state
    if (showOnboarding) return           // onboarding wizard is active — wait
    if (user.work_title) return          // already completed
    if (localStorage.getItem('sw-profile-completion-skipped')) return
    setProfileComplete(false)
  }, [user, settingsLoaded, showOnboarding])

  // ── WebSocket: site_created notification ──
  // Keep the message handler in a ref so the WebSocket callback always calls
  // the current version of showToast/t/toggleSite/trackRecent without needing
  // to tear down and reconnect the socket whenever those values change.
  wsMessageHandlerRef.current = (event) => {
    try {
      const msg = JSON.parse(event.data)
      if (msg.type === 'site_created' && msg.site) {
        const site = msg.site
        showToast(t('siteCreatedToast').replace('{{name}}', site.name), 'info', {
          action: {
            label: t('siteCreatedAdd'),
            onClick: () => {
              toggleSite(site.id)
              trackRecent(site.id)
            },
          },
        })
      }
    } catch { /* ignore parse errors */ }
  }

  useEffect(() => {
    if (!user) return
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    let ws: WebSocket | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined

    const connect = () => {
      try {
        ws = new WebSocket(`${protocol}//${window.location.host}/ws`)
        ws.onmessage = (event) => wsMessageHandlerRef.current && wsMessageHandlerRef.current(event)
        ws.onclose = () => {
          reconnectTimer = setTimeout(connect, 5000)
        }
        ws.onerror = () => {
          ws?.close()
        }
      } catch { /* WebSocket not available */ }
    }

    connect()
    return () => {
      clearTimeout(reconnectTimer)
      if (ws) ws.close()
    }
  }, [user])

  // ── Cmd+K shortcut ──
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdKOpen(o => !o)
      }
      if (e.key === 'Escape' && cmdKOpen) setCmdKOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [cmdKOpen])

  useEffect(() => {
    if (cmdKOpen && cmdKRef.current) cmdKRef.current.focus()
  }, [cmdKOpen])

  // ── Global keyboard shortcuts ──
  useKeyboardShortcuts([
    {
      key: 'g',
      handler: () => setCmdKOpen(o => !o),
    },
    {
      key: 'Escape',
      alwaysFire: true,
      handler: (e) => {
        // UX-12 — never re-handle our own forwarded Escape. The synthetic
        // event below bubbles back up to this document-level handler; if the
        // dialog doesn't close itself synchronously we'd find it again and
        // re-dispatch forever (RangeError: Maximum call stack size exceeded).
        if ((e as globalThis.KeyboardEvent & { __escForwarded?: boolean }).__escForwarded) return
        if (cmdKOpen) {
          setCmdKOpen(false)
          return
        }
        const openModal = document.querySelector('[data-modal-open="true"], [role="dialog"]')
        if (openModal) {
          const forwarded = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }) as
            globalThis.KeyboardEvent & { __escForwarded?: boolean }
          forwarded.__escForwarded = true
          openModal.dispatchEvent(forwarded)
        }
      },
    },
  ])

  // ── Mobile menu: focus trap, Escape key ──
  useEffect(() => {
    if (!menuOpen) return
    const menu = menuRef.current
    if (menu) {
      const closeBtn = menu.querySelector('button')
      if (closeBtn) closeBtn.focus()
    }
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        menuTriggerRef.current?.focus()
        return
      }
      if (e.key !== 'Tab') return
      const focusable = menu?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable || focusable.length === 0) return
      const first = focusable[0] as HTMLElement
      const last  = focusable[focusable.length - 1] as HTMLElement
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Brighella v2 (ADR-013 #6) — bind density preference to <html data-density>
  // so descendants can theme via [data-density="compact"] selectors.
  // BILL-11: 80% usage nudge surfaced globally for admins
  const { shouldNudge, nearCapResources } = useUsageNudge()
  const [nudgeDismissed, setNudgeDismissed] = useState<boolean>(false)
  const isAdminUser = useAuthStore(s => s.isAdmin?.() ?? false)

  const density = useCohesionStore((s) => s.density)
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density)
    return () => {
      // Component lives for the whole authed session; reset on unmount is
      // a safety net for tests that unmount AppLayout.
      document.documentElement.removeAttribute('data-density')
    }
  }, [density])

  return (
    <StepUpProvider>
    <div className="app-shell">
      <a href="#main-content" className="skip-link">
        {t('skipToContent')}
      </a>
      <AppSidebar
        sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}
        logoUrl={logoUrl} appName={appName}
        searchQuery={searchQuery} onSearch={handleSearch}
        searchResults={searchResults} showSearchDropdown={showSearchDropdown}
        setShowSearchDropdown={setShowSearchDropdown}
        searchHighlight={searchHighlight} setSearchHighlight={setSearchHighlight}
        onSearchSelect={handleSearchSelect} onSearchKeyDown={handleSearchKeyDown}
        theme={theme} toggleTheme={toggleTheme}
        getThemeTitle={getThemeTitle}
        onShowProfile={() => setShowProfile(true)} user={user}
        lang={lang} setLang={setLang} languageOptions={languageOptions}
        platformMode={platformMode}
        onOpenCommandPalette={() => setCmdKOpen(true)}
      />

      {/* ADR-014 D11 — sticky offline banner above the topbar. */}
      <OfflineBanner />

      <main id="main-content" tabIndex={-1} className={cls('main-content', sidebarCollapsed && 'main-content-collapsed')}>
        <MobileTopbar
          mobileSearchOpen={mobileSearchOpen} setMobileSearchOpen={setMobileSearchOpen}
          searchQuery={searchQuery} onSearch={handleSearch}
          searchResults={searchResults} showSearchDropdown={showSearchDropdown}
          searchHighlight={searchHighlight} setSearchHighlight={setSearchHighlight}
          onSearchSelect={handleSearchSelect}
          logoUrl={logoUrl} appName={appName}
          theme={theme} toggleTheme={toggleTheme} getThemeEmoji={getThemeEmoji}
          lang={lang} setLang={setLang} languageOptions={languageOptions}
          menuTriggerRef={menuTriggerRef} menuOpen={menuOpen} setMenuOpen={setMenuOpen}
        />
        <ImpersonationBanner />
        <PastDueBanner />
        {/* Ziik P3 — app-wide must-read banner. Self-hides when empty; only
            for authenticated non-guest users (matches sibling banner gating). */}
        {user && user.role !== 'guest' && <MustReadBanner />}
        {/* BILL-11: 80% usage nudge — visible to admins only, dismissible per session */}
        {isAdminUser && shouldNudge && !nudgeDismissed && (
          <div role="alert" data-testid="usage-nudge-banner" style={{
            background: 'var(--warning-dim, #fef3c7)',
            borderBottom: '1px solid var(--warning)',
            padding: '8px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: 'var(--text-sm)',
          }}>
            <span>{t('billingNudgeNearLimit').replace('{resources}', nearCapResources.join(', '))}</span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 16 }}>
              <a href="/settings?tab=billing" style={{ fontWeight: 600, color: 'var(--warning)', textDecoration: 'none' }}>{t('viewPlans')}</a>
              <button
                type="button"
                onClick={() => setNudgeDismissed(true)}
                aria-label={t('dismiss')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1 }}
              >✕</button>
            </div>
          </div>
        )}
        <SiteFilterBanner />
        {/* Full-bleed surfaces (chat-style apps) opt out of the centered
            1200px .page-content column so they use the whole viewport width.
            Reusable for the app-wide wide-mode pass; comms is the first. */}
        <div className={cls('page-content', location.pathname.startsWith('/comms') && 'page-content--full')}>{children}</div>
      </main>

      <MobileBottomNav overdueCount={overdueCount} />

      {/* Brighella v2 (ADR-013 #4) — universal entity drawer.
          Reads cohesion.drawer; click any entity name → openDrawer({…})
          and the per-type panel renders. Mounted once at the app root. */}
      <EntityDrawer />

      {/* Brighella v2 (ADR-014 D12) — universal undo toast.
          Mutations push a revision into useUndoStore; this surface offers
          Undo for the reversibility window or Cmd+Z anywhere. */}
      <UndoToast />

      {/* Brighella v2 (ADR-013 #8) — `?` opens the shortcut overlay. */}
      <HelpModeKeyListener />
      <KeyboardHelp />

      {/* Brighella v2 (ADR-014 D21) — first-login product tour. The component
          fetches /api/users/me/ui-preferences and stays silent if dismissed. */}
      <OnboardingTour onClose={undefined} />

      <MobileMenu
        menuOpen={menuOpen} setMenuOpen={setMenuOpen} menuRef={menuRef}
        appName={appName} user={user}
      />

      {!profileComplete && <ProfileCompletionModal user={user} onComplete={() => setProfileComplete(true)} onUserUpdate={updateUser} />}

      <UserProfileModal open={showProfile} onClose={() => setShowProfile(false)} user={user} />

      {showOnboarding && (
        <OnboardingWizard onComplete={() => {
          setShowOnboarding(false)
          navigate('/')
        }} />
      )}

      <FloatingActions />

      <DemoSidebar />

      <QAOverlay />

      <CmdKSearch
        cmdKOpen={cmdKOpen} setCmdKOpen={setCmdKOpen} cmdKRef={cmdKRef}
        searchQuery={searchQuery} onSearch={handleSearch}
        searchResults={searchResults}
        searchHighlight={searchHighlight} setSearchHighlight={setSearchHighlight}
        onCmdKSelect={handleCmdKSelect}
      />

    </div>
    </StepUpProvider>
  )
}
